import React from 'react';
import styled from 'styled-components';
import Title from '@/components/Title';
import { graphql } from 'gatsby';
import { config } from '@keytrade/functions';

import Container from '@/components/Container';
import BlockWrapper from '@/components/BlockWrapper';
import { getBackgroundColor, isDarkTheme } from '@/utils/StyleUtils';
import Link, { LinkStyle, LinkType } from '../Link';
import Paragraph from '../Paragraph';
import { mediaQuery } from '@/utils/helpers';
import RichText, { RichTextType } from '@/components/Richtext';

const { list: colors } = config.colors;

const StepsWrapper = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  padding: 0;
  list-style: none;
  gap: 2.4rem;
  counter-reset: steps;
  margin-bottom: 4rem;
  ${mediaQuery.md(`
  margin-bottom: 7.2rem`)}
`;

const StepLinkWrapper = styled.div`
  margin-top: auto;
  padding-top: 3.2rem;
  a {
    text-align: center;
  }
`;

type StepBlockProps = {
  highlight: boolean;
};

const StepBlock = styled.li<StepBlockProps>`
  padding: 3.2rem;
  border-radius: 2.4rem;
  display: flex;
  flex-direction: column;
  &:before {
    display: block;
    counter-increment: steps;
    content: counter(steps);
    line-height: 1;
    font-size: 9.6rem;
    margin: 0 0 2.4rem;
    ${({ highlight }) =>
      highlight
        ? `
          color: ${colors.BlueSky}
        `
        : `
          color: ${colors.GreyBlue}
  `}
  }
  ${({ highlight }) =>
    highlight
      ? `
background: ${getBackgroundColor('White')};
border: 1px solid ${getBackgroundColor('Grey Background')};
box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.05),
0px 2px 7px 0px rgba(0, 0, 0, 0.05);
`
      : `
background: ${getBackgroundColor('Blue Fog')}
`}
`;

const LinksWrapper = styled.div`
  text-align: center;
`;

const BlockContent = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  ${mediaQuery.md(`
  margin-bottom: 7rem;`)}
`;

type StepType = {
  title: string;
  text: RichTextType;
  link: LinkType;
};
export type StepsBlockType = {
  __typename?: string;
  id: string;
  title: string;
  subtitleMultipleLine: {
    subtitle: string;
  };
  backgroundColor: string;
  firstOneHighlighted: boolean;
  steps: Array<StepType>;
  link: LinkType;
};

type Props = StepsBlockType;
const StepsBlock: React.FC<Props> = ({
  id,
  title,
  subtitleMultipleLine,
  backgroundColor,
  steps,
  firstOneHighlighted,
  link,
}) => {
  return (
    <BlockWrapper id={id} color={backgroundColor}>
      <Container>
        <BlockContent>
          <Title
            size='xl'
            level='h2'
            margin='0 0 2rem'
            lightColor={isDarkTheme(backgroundColor)}
          >
            {title}
          </Title>
          {subtitleMultipleLine && (
            <Paragraph
              color={isDarkTheme(backgroundColor) ? 'White' : 'GreyBlue'}
              weight='semiBold'
            >
              {subtitleMultipleLine.subtitle}
            </Paragraph>
          )}
        </BlockContent>
        {steps && (
          <StepsWrapper>
            {React.Children.toArray(
              steps.map((step, index) => {
                if (!step) return null;
                return (
                  // eslint-disable-next-line react/jsx-key
                  <StepBlock
                    highlight={
                      !firstOneHighlighted ||
                      (firstOneHighlighted && index === 0)
                    }
                  >
                    <Title size='sm' level='h3' margin='0 0 2rem'>
                      {step.title}
                    </Title>
                    <RichText data={step.text} />
                    <StepLinkWrapper>
                      <Link
                        fullWidth
                        {...step.link}
                        linkStyle={LinkStyle.PRIMARY}
                      />
                    </StepLinkWrapper>
                  </StepBlock>
                );
              }),
            )}
          </StepsWrapper>
        )}
        <LinksWrapper>
          <Link {...link} linkStyle={LinkStyle.PRIMARY} />
        </LinksWrapper>
      </Container>
    </BlockWrapper>
  );
};

export default StepsBlock;
export const stepsBlockFragmentQuery = graphql`
  fragment StepsBlockFragment on ContentfulStepsBlock {
    id
    __typename
    title
    subtitleMultipleLine: subtitle {
      subtitle
    }
    link {
      ...LinkFragment
    }
    firstOneHighlighted
    backgroundColor
    steps {
      title
      text {
        ...RichTextFragment
      }
      link {
        ...LinkFragment
      }
    }
  }
`;
